import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, Subscription, map, catchError } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { WEIGHT_OPTIONS, ORDER_NUMBER_OPTIONS, PRODUCT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { Order } from '../../models/order';
import { OrderService } from '../../services/order.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ARTICLE, CUSTOMER, GROSS, CDD } from '../../../shared/models/settings';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

const sortWay = OrderByDirection.DESC;
const sortField = CDD;

@Component({
	selector: 'copo-order-tracking-list',
	templateUrl: './search-order-list.component.html',
	animations: pageTransitions,
	styleUrl: './search-order-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		RouterLink,
		MatIconModule,
		NgIf,
		LoadingSpinnerComponent,
		NumberPipe,
		DimensionPipe
	]
})
export class SearchOrderListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	productDisplay: keyof typeof PRODUCT_OPTIONS = ARTICLE;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	filter: string | undefined;

	readonly UNDERSCORE = UNDERSCORE;

	requiredFields: (keyof Order)[] = [
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'thickness',
		'width',
		'length',
		'cddWeek'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly orderService: OrderService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {
		this.user = this.userService.user;
		this.requiredFields.push(
			'grossQtyOrdered',
			'grossQtyInProduction',
			'grossQtyAvailableReadyToShip',
			'grossQtyAvailableInTransit',
			'grossQtyDelivered',
			'steelgrade',
			'customerOrderNumber',
			'customerOrderItemNumber',
			'steelgradeDescription',
			'productDescription'
		);

		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined>p['query'];
			this.filter = searchText ? this.orderService.createLuceneFilter(searchText) : undefined;
		});
	}

	get dataId() {
		const dataId = 'order-tracking';
		return this.filter === undefined ? dataId : `${ dataId }-${ this.filter }`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Order[]>) => void) {
		provideData(
			this.orderService.getOrders(
				this.requiredFields,
				this.filter,
				index,
				limit,
				new OrderBy(sortField, sortWay),
				false
			).pipe(
				map(res => res.results),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}

	getProductName(order: Order) {
		return order.steelgradeDescription || UNDERSCORE;
	}

	getProductType() {
		return 'order-tracking.steelgrade';
	}

	getOrderName(order: Order) {
		return order.customerOrderNumber === ''
			? UNDERSCORE
			: `${ order.customerOrderNumber }/${ order.customerOrderItemNumber }`;
	}

	getQuantity(order: Order, quantityName: string) {
		const qtyOrdered = order.grossQtyOrdered;
		const qtyInProduction = order.grossQtyInProduction;
		const qtyAvailableReadyToShip = order.grossQtyAvailableReadyToShip;
		const qtyAvailableInTransit = order.grossQtyAvailableInTransit;
		const qtyDelivered = order.grossQtyDelivered;
		switch (quantityName) {
		case 'ordered':
			return qtyOrdered || 0;
		case 'inProduction':
			return qtyInProduction || 0;
		case 'readyToShip':
			return qtyAvailableReadyToShip || 0;
		case 'inTransit':
			return qtyAvailableInTransit || 0;
		default:
			return qtyDelivered || 0;
		}
	}

	checkCddWeekData(order: Order) {
		return this.orderService.checkCddWeekData(order.cddWeek);
	}
}
